import { z } from "zod";
import { PlaidConnectionErrors } from "@common/constants";
import { utcDatetime } from "@common/helpers/date";
import { userResponseSchema } from "@common/schemas/user.schema";
import {
    ConnectionProviderType,
    FinancialConnectionBase,
} from "@common/types/financialConnection";
import {
    StripeConnectionKind,
    StripeConnectionScope,
} from "@common/types/stripeConnection";

const financialConnectionResponseBaseSchema = z.object({
    id: z.number(),
    connectionExternalId: z.string(),
    createdAt: utcDatetime,
    user: userResponseSchema.nullable(),
    userId: z.number().nullable(),
    error: z.string().nullable(),
    // Never loaded on nested connections, but Plaid/Stripe type guards
    // downstream assume the array exists.
    accounts: z.tuple([]).default([]),
});

const plaidConnectionResponseSchema =
    financialConnectionResponseBaseSchema.extend({
        connectionProvider: z.literal(ConnectionProviderType.PLAID),
        error: z.nativeEnum(PlaidConnectionErrors).nullable(),
        bankName: z.string(),
        bankLogoUrl: z.string().nullable(),
        institutionId: z.string().nullable(),
        historicalDataProvidedManually: z.boolean(),
        statementsApiSupported: z.boolean().nullable(),
        statementsApiEnabled: z.boolean().nullable(),
    });

const stripeConnectionResponseSchema =
    financialConnectionResponseBaseSchema.extend({
        connectionProvider: z.literal(ConnectionProviderType.STRIPE),
        scope: z.nativeEnum(StripeConnectionScope),
        kind: z.nativeEnum(StripeConnectionKind),
        detailsSubmitted: z.boolean(),
        chargesEnabled: z.boolean(),
        isAbandoned: z.boolean(),
    });

const otherFinancialConnectionResponseSchema =
    financialConnectionResponseBaseSchema.extend({
        connectionProvider: z.enum([
            ConnectionProviderType.GUSTO,
            ConnectionProviderType.MERCURY,
            ConnectionProviderType.RAMP,
        ]),
    });

const financialConnectionVariantsResponseSchema = z.discriminatedUnion(
    "connectionProvider",
    [
        plaidConnectionResponseSchema,
        stripeConnectionResponseSchema,
        otherFinancialConnectionResponseSchema,
    ],
);

// Legacy connections are attached to integration accounts outside TypeORM
// relations, so servers only hold a FinancialConnectionBase; the provider
// variant is enforced when the response is parsed.
export const financialConnectionResponseSchema = z
    .custom<FinancialConnectionBase>(
        (connection) => typeof connection === "object" && connection !== null,
    )
    .pipe(financialConnectionVariantsResponseSchema);
